import React, { useState } from 'react';
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Alert, Spinner } from 'reactstrap';
import { customerSearchRequest } from '../../api/controller/CustomerSearchController';
import logo from '../../assets/images/logo.svg';

const STATUS_COLORS = {
    'On Hand':    { bg: '#dbeafe', color: '#1e40af' },
    'Received':   { bg: '#dcfce7', color: '#166534' },
    'Delivered':  { bg: '#d1fae5', color: '#065f46' },
    'Forwarded':  { bg: '#fef9c3', color: '#854d0e' },
    'Closed':     { bg: '#f3f4f6', color: '#374151' },
};

const StatusBadge = ({ status }) => {
    const style = STATUS_COLORS[status] ?? { bg: '#f3f4f6', color: '#374151' };
    return (
        <span style={{
            display: 'inline-block',
            background: style.bg,
            color: style.color,
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
        }}>
            {status}
        </span>
    );
};

const TypeBadge = ({ type }) => {
    const styles = {
        AHL:    { bg: '#dbeafe', color: '#1e3a8a' },
        CLAIMS: { bg: '#dcfce7', color: '#14532d' },
    };
    const s = styles[type] ?? { bg: '#f3f4f6', color: '#374151' };
    return (
        <span style={{
            display: 'inline-block',
            background: s.bg,
            color: s.color,
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
        }}>
            {type}
        </span>
    );
};

const CustomerSearch = () => {
    const [form, setForm] = useState({ reference: '', last_name: '' });
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setResults(null);
        setSearched(false);

        if (!form.reference.trim()) {
            setError('Please enter a reference number.');
            return;
        }
        if (!form.last_name.trim()) {
            setError('Please enter your last name.');
            return;
        }

        setLoading(true);
        const response = await customerSearchRequest({
            reference: form.reference.trim(),
            last_name: form.last_name.trim(),
        });
        setLoading(false);
        setSearched(true);

        if (response?.error) {
            setError(response.error === 'Too Many Attempts.'
                ? 'Too many search attempts. Please wait a moment and try again.'
                : 'An error occurred. Please try again.');
            return;
        }

        setResults(response?.results ?? []);
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex', flexDirection: 'column' }}>

            {/* Top bar */}
            <div style={{ background: '#0f172a', padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <img src={logo} alt="Logo" style={{ height: 28, opacity: 0.9 }} />
                <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500, letterSpacing: '0.04em' }}>
                    BAGGAGE TRACKING
                </span>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px 32px' }}>
                <div style={{ width: '100%', maxWidth: 600 }}>

                    {/* Title */}
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 52, height: 52, borderRadius: '50%',
                            background: '#0ea5e9', marginBottom: 16,
                        }}>
                            <i className="mdi mdi-bag-personal" style={{ fontSize: 26, color: '#fff' }}></i>
                        </div>
                        <h4 style={{ fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
                            Baggage Status Search
                        </h4>
                        <p style={{ color: '#64748b', fontSize: 14, marginTop: 6, marginBottom: 0 }}>
                            Track your delayed or mishandled baggage using your reference number and last name.
                        </p>
                    </div>

                    {/* Search card */}
                    <Card style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)', borderRadius: 10 }}>
                        <CardBody style={{ padding: '28px 32px' }}>
                            <Form onSubmit={handleSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                                    <FormGroup style={{ marginBottom: 20 }}>
                                        <Label style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
                                            Reference Number
                                        </Label>
                                        <Input
                                            type="text"
                                            placeholder="e.g. MELQF12345"
                                            value={form.reference}
                                            onChange={e => setForm({ ...form, reference: e.target.value.toUpperCase() })}
                                            style={{ borderRadius: 6, fontSize: 14, height: 42 }}
                                        />
                                        <small style={{ color: '#9ca3af', fontSize: 11, marginTop: 4, display: 'block' }}>
                                            Reference number given to you
                                        </small>
                                    </FormGroup>
                                    <FormGroup style={{ marginBottom: 20 }}>
                                        <Label style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
                                            Last Name
                                        </Label>
                                        <Input
                                            type="text"
                                            placeholder="Last Name"
                                            value={form.last_name}
                                            onChange={e => setForm({ ...form, last_name: e.target.value.toUpperCase() })}
                                            style={{ borderRadius: 6, fontSize: 14, height: 42 }}
                                        />
                                    </FormGroup>
                                </div>

                                {error && (
                                    <Alert color="danger" style={{ borderRadius: 6, fontSize: 13, padding: '10px 14px', marginBottom: 16 }}>
                                        <i className="mdi mdi-alert-circle-outline me-1"></i>{error}
                                    </Alert>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{
                                        width: '100%', height: 44, borderRadius: 6, border: 'none',
                                        background: loading ? '#93c5fd' : '#0ea5e9',
                                        color: '#fff', fontWeight: 600, fontSize: 14,
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    {loading
                                        ? <><Spinner size="sm" /><span>Searching...</span></>
                                        : <><i className="mdi mdi-magnify" style={{ fontSize: 18 }}></i><span>Search Baggage</span></>
                                    }
                                </button>
                            </Form>
                        </CardBody>
                    </Card>

                    {/* Results */}
                    {searched && results !== null && (
                        <div style={{ marginTop: 24 }}>
                            {results.length === 0 ? (
                                <Card style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderRadius: 10 }}>
                                    <CardBody style={{ padding: 32, textAlign: 'center' }}>
                                        <i className="mdi mdi-bag-personal-off" style={{ fontSize: 36, color: '#d1d5db', display: 'block', marginBottom: 10 }}></i>
                                        <p style={{ color: '#6b7280', fontSize: 14, margin: 0, fontWeight: 500 }}>
                                            No records found
                                        </p>
                                        <p style={{ color: '#9ca3af', fontSize: 12, margin: '4px 0 0' }}>
                                            Please check your reference number and last name and try again.
                                        </p>
                                    </CardBody>
                                </Card>
                            ) : (() => {
                                const r = results[0];
                                return (
                                    <Card style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)', borderRadius: 10, overflow: 'hidden' }}>
                                        {/* Card header */}
                                        <div style={{ background: '#0f172a', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 15, letterSpacing: '0.01em' }}>
                                                    {r.reference}
                                                </span>
                                                <TypeBadge type={r.type} />
                                            </div>
                                            <StatusBadge status={r.status} />
                                        </div>

                                        {/* Detail grid */}
                                        <CardBody style={{ padding: '20px 24px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '18px 24px', marginBottom: 20 }}>
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <DetailField label="Hotel / Address" value={r.hotel} icon="mdi-home-city-outline" />
                                                </div>
                                                <DetailField label="Flight Date" value={r.flight_date} icon="mdi-calendar-outline" />
                                                <DetailField label="Flight Number" value={r.flight} icon="mdi-airplane" accent />
                                                <DetailField label="Number of Bags" value={r.bags} icon="mdi-bag-suitcase" large />
                                            </div>

                                            {/* Bag details */}
                                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                                                    <i className="mdi mdi-tag-multiple-outline" style={{ fontSize: 13, color: '#94a3b8' }}></i>
                                                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                                                        Bag Details
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                    {r.bag_details.map((b, i) => (
                                                        <div key={i} style={{
                                                            display: 'flex', alignItems: 'center', gap: 10,
                                                            background: '#f8fafc', border: '1px solid #e2e8f0',
                                                            borderRadius: 8, padding: '10px 14px',
                                                        }}>
                                                            <i className="mdi mdi-barcode" style={{ fontSize: 18, color: '#94a3b8' }}></i>
                                                            <div>
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', letterSpacing: '0.02em' }}>
                                                                    {b.tag}
                                                                </div>
                                                                <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                    <StatusBadge status={b.status} />
                                                                    {b.arriving_flight && (
                                                                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                                                                            <i className="mdi mdi-airplane-landing" style={{ marginRight: 2 }}></i>
                                                                            {b.arriving_flight}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                );
                            })()}
                        </div>
                    )}

                    {/* Footer note */}
                    <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 28 }}>
                        <i className="mdi mdi-shield-check-outline me-1"></i>
                        Records are only available for flights within the last 12 months.
                    </p>
                </div>
            </div>
        </div>
    );
};

const DetailField = ({ label, value, icon, large, accent }) => (
    <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
            <i className={`mdi ${icon}`} style={{ fontSize: 13, color: '#94a3b8' }}></i>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                {label}
            </span>
        </div>
        <span style={{
            fontSize: large ? 22 : 14,
            fontWeight: large ? 700 : 600,
            color: accent ? '#0ea5e9' : '#1e293b',
            lineHeight: 1.3,
        }}>
            {value}
        </span>
    </div>
);

export default CustomerSearch;
